import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { NafCode } from '../nouveauxAdherents/NafCodeSelector';
import NafCodeSelector from '../nouveauxAdherents/NafCodeSelector';
import { CreateNafListData, NafList, UpdateNafListData } from '../../types/nafList';

interface NafListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateNafListData | UpdateNafListData) => Promise<void>;
  nafList?: NafList | null;
  nafCodes: NafCode[];
  isLoading?: boolean;
}

const NafListModal: React.FC<NafListModalProps> = ({
  isOpen,
  onClose,
  onSave,
  nafList,
  nafCodes,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    nafCodes: [] as string[],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const isEditing = !!nafList;

  // Reset form when modal opens/closes or nafList changes
  useEffect(() => {
    if (isOpen) {
      if (nafList) {
        setFormData({
          name: nafList.name,
          description: nafList.description || '',
          nafCodes: [...nafList.nafCodes],
        });
      } else {
        setFormData({
          name: '',
          description: '',
          nafCodes: [],
        });
      }
      setErrors({});
    }
  }, [isOpen, nafList]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom de la liste est requis';
    }

    if (formData.nafCodes.length === 0) {
      newErrors.nafCodes = 'Au moins un code NAF est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      const submitData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        nafCodes: formData.nafCodes,
      };

      await onSave(submitData);
      onClose();
    } catch (error) {
      console.error('Error saving NAF list:', error);
      // Error handling is done in the parent component
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[1001]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {isEditing ? 'Modifier la liste NAF' : 'Créer une nouvelle liste NAF'}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={handleClose}
                    disabled={isSaving}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name field */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Nom de la liste *
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className={`w-full rounded-md border ${
                        errors.name ? 'border-red-300' : 'border-gray-300'
                      } px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500`}
                      placeholder="Ex: Secteur BTP, Commerce de détail..."
                      disabled={isSaving}
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>

                  {/* Description field */}
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                      Description (optionnelle)
                    </label>
                    <textarea
                      id="description"
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Description de la liste et de son utilisation..."
                      disabled={isSaving}
                    />
                  </div>

                  {/* NAF codes selector */}
                  <div className="!mb-16">
                    <NafCodeSelector
                      nafCodes={nafCodes}
                      selectedNafCodes={formData.nafCodes}
                      onChange={(codes) => setFormData(prev => ({ ...prev, nafCodes: codes }))}
                      disabled={isSaving || isLoading}
                    />
                    {errors.nafCodes && (
                      <p className="mt-1 text-sm text-red-600">{errors.nafCodes}</p>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={isSaving}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={isSaving || isLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      {isSaving ? 'Enregistrement...' : (isEditing ? 'Modifier' : 'Créer')}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default NafListModal; 